import { createContext, useContext, useState, useRef, useCallback } from 'react';

// Create the context
const GlobalAudioContext = createContext();

// Provider component
export const GlobalAudioProvider = ({ children }) => {
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const currentAudioRef = useRef(null);

  const playAudio = useCallback((audioId, audioElement) => {
    // Stop any currently playing audio
    if (currentAudioRef.current && currentAudioRef.current !== audioElement) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
    }

    // Update the currently playing audio
    setCurrentlyPlaying(audioId);
    currentAudioRef.current = audioElement;
  }, []);

  const stopAudio = useCallback((audioId) => {
    if (currentlyPlaying === audioId) {
      setCurrentlyPlaying(null);
      currentAudioRef.current = null;
    }
  }, [currentlyPlaying]);

  const stopAllAudio = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
    }
    setCurrentlyPlaying(null);
    currentAudioRef.current = null;
  }, []);

  const isPlaying = useCallback((audioId) => {
    return currentlyPlaying === audioId;
  }, [currentlyPlaying]);

  return (
    <GlobalAudioContext.Provider value={{
      currentlyPlaying,
      playAudio,
      stopAudio,
      stopAllAudio,
      isPlaying
    }}>
      {children}
    </GlobalAudioContext.Provider>
  );
};

// Hook to use the global audio manager
export const useGlobalAudioManager = () => {
  const context = useContext(GlobalAudioContext);
  if (!context) {
    throw new Error('useGlobalAudioManager must be used within a GlobalAudioProvider');
  }
  return context;
};
